import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon, CheckCircleIcon, CloseIcon } from '@src/assets/icons';
import {
  PROGRESS_BAR_CHECK_ITEMS,
  QUIZ_STEPS_HISTORY,
  TOTAL_STEPS,
} from '@src/constants';
import { useQuizService } from '@src/hooks/useQuizService';

import { HeaderLanguageDropdown } from './HeaderLanguageDropdown';
import { HeaderLogo } from './HeaderLogo';
import { IHeader } from './interfaces';

export const Header = ({
  showProgress,
  showBack,
  showClose,
  extraContent,
  containerClassName,
  contentClassName,
  hideLogoMobile = false,
  clickableLogo = true,
  logoProps,
  logoContainerClassName,
  progressBarWidth: parentProgressBarWidth,
  onNavBack,
  onNavToProfile,
}: IHeader) => {
  const { stepIndex, prevStep } = useQuizService();

  const currentQuizStepNumber = QUIZ_STEPS_HISTORY.slice(
    0,
    stepIndex + 1,
  ).length;

  const progressBarWidth =
    parentProgressBarWidth ??
    Math.round((currentQuizStepNumber / TOTAL_STEPS) * 100);

  const progressItems = useMemo(() => {
    const arr = Array.from({ length: PROGRESS_BAR_CHECK_ITEMS });

    return arr.map((_, index, all) => {
      const allItems = all.length - 1;
      const isFirst = index === 0;
      const isLast = index === allItems;

      let isActive = progressBarWidth > (100 / allItems) * index;

      if (isFirst) isActive = true;
      if (isLast) isActive = progressBarWidth > 99;

      return { isActive, index };
    });
  }, [progressBarWidth]);

  return (
    <header
      className={twMerge(
        'md:mb-9 flex w-full flex-col items-center md:pt-3',
        containerClassName,
      )}
    >
      <div
        className={twMerge(
          'relative flex h-15 w-full items-center max-sm:items-center pb-5 justify-center mt-3',
          contentClassName,
          extraContent && 'h-fit',
        )}
      >
        {!extraContent && showBack && (
          <button
            className="absolute left-0 text-black duration-200 hover:text-blue-9 max-sm:-ml-2"
            onClick={onNavBack ?? prevStep}
          >
            <ArrowIcon className="h-6 w-7 rotate-180" fill="currentColor" />
          </button>
        )}
        <div
          className={twMerge(
            'flex w-max items-center justify-center',
            logoContainerClassName,
            hideLogoMobile && 'max-sm:hidden',
          )}
        >
          <HeaderLogo
            clickableLogo={clickableLogo}
            logoProps={logoProps}
            extraContent={extraContent}
          />
        </div>
        <HeaderLanguageDropdown />
        {extraContent}

        {!extraContent && showClose && (
          <button onClick={onNavToProfile} className="absolute p-1 right-0">
            <CloseIcon className="size-5" />
          </button>
        )}
      </div>
      {showProgress && (
        <div className={'w-full'}>
          <div className="h-[0.313rem] w-full bg-blue-2">
            <div
              className="h-full bg-blue-1 duration-200"
              style={{ width: `${progressBarWidth}%` }}
            />
          </div>
          <div className="-mt-3 flex justify-between">
            {progressItems.map(({ isActive, index }) => (
              <div
                key={'ProgressCheck' + index}
                className="overflow-hidden rounded-full bg-white"
              >
                {isActive ? (
                  <CheckCircleIcon className="size-5 fill-blue-1" />
                ) : (
                  <div className="size-5 rounded-full border-[3px] border-blue-2" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};
